import type { ChangelogCandidateEntry } from "@/features/changelog/api/changelog.api";

/**
 * Roundups are written on a rhythm — end of the week, end of the fortnight, end
 * of the sprint — so the compose sheet offers those three rather than making
 * somebody tick their way to the same answer every time.
 */
export type ChangelogTemplateId = "week" | "fortnight" | "sprint";

export const CHANGELOG_TEMPLATE_ORDER: ChangelogTemplateId[] = [
  "week",
  "fortnight",
  "sprint",
];

/** Half-open: a deploy at exactly `end` belongs to the next window. */
export interface DateWindow {
  start: Date;
  end: Date;
}

export interface ChangelogTemplateWindow {
  id: ChangelogTemplateId;
  window: DateWindow;
  /** Suggested name for the roundup; the admin can overwrite it. */
  title: string;
}

export interface ChangelogTemplate extends ChangelogTemplateWindow {
  /** How many candidate deploys fall inside, so a dead template can be disabled. */
  entryCount: number;
  itemCount: number;
}

/**
 * Where sprint numbering starts and how long a sprint runs.
 *
 * Numbers are counted from an anchor rather than read from Jira, which this app
 * does not talk to. The anchor is a setting because a team that re-plans its
 * cadence should not need a deploy to say so; a wrong value is visible before
 * it does any damage, since the compose sheet shows the dates the template
 * covers and the title stays editable.
 */
export interface SprintConfig {
  /** `YYYY-MM-DD` — the first day of the sprint numbered `anchorNumber`. */
  anchorDate: string;
  anchorNumber: number;
  lengthDays: number;
}

/** What the cadence was before it was configurable, and what a caller without
 * the settings — the compose sheet still opens for an admin who cannot read
 * them — falls back to. */
export const DEFAULT_SPRINT_CONFIG: SprintConfig = {
  anchorDate: "2026-07-28",
  anchorNumber: 2,
  lengthDays: 14,
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Parsed as a local midnight. `new Date("2026-07-28")` is parsed as UTC, which
 * lands the anchor on the 27th for anyone west of it and shifts every sprint
 * boundary by a day.
 */
function parseAnchorDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  return Number.isNaN(parsed.getTime()) ? new Date(DEFAULT_SPRINT_CONFIG.anchorDate) : parsed;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Whole days between two midnights, safe across daylight-saving shifts. */
function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

/** The sprint that contains `at`, counting forwards and back from the anchor. */
export function getSprintAt(
  at: Date,
  sprint: SprintConfig = DEFAULT_SPRINT_CONFIG,
): { number: number; window: DateWindow } {
  const anchor = parseAnchorDate(sprint.anchorDate);
  const length = Math.max(1, sprint.lengthDays);
  const elapsed = Math.floor(daysBetween(anchor, startOfDay(at)) / length);
  const start = addDays(anchor, elapsed * length);

  return {
    number: sprint.anchorNumber + elapsed,
    window: { start, end: addDays(start, length) },
  };
}

/** The last `days` calendar days, today included. */
function trailingWindow(now: Date, days: number): DateWindow {
  const end = addDays(startOfDay(now), 1);
  return { start: addDays(end, -days), end };
}

export function isWithinWindow(isoDate: string, window: DateWindow): boolean {
  const at = new Date(isoDate).getTime();
  return at >= window.start.getTime() && at < window.end.getTime();
}

export function itemIdsInWindow(
  entries: ChangelogCandidateEntry[],
  window: DateWindow,
): string[] {
  return entries
    .filter((entry) => isWithinWindow(entry.deployedAt, window))
    .flatMap((entry) => entry.items.map((item) => item.id));
}

/**
 * "28 July – 10 August", collapsing the repeated month to "2 – 8 August". The
 * end is stepped back a day because the window is half-open and nobody reads
 * "2 – 9 August" as a week.
 *
 * `formatRange` does the collapsing, because which end of the range keeps the
 * month depends on the locale: dropping it from the start gives "2 – 8 August"
 * in en-GB and the unreadable "3 – August 9" in en-US.
 */
export function formatWindow(window: DateWindow, locale: string): string {
  const lastDay = addDays(window.end, -1);

  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
  }).formatRange(window.start, lastDay);
}

function templateTitle(
  id: ChangelogTemplateId,
  window: DateWindow,
  locale: string,
  sprint: SprintConfig,
): string {
  if (id === "sprint") return `Sprint ${getSprintAt(window.start, sprint).number}`;
  if (id === "week") {
    return `Week of ${window.start.toLocaleDateString(locale, { day: "numeric", month: "long" })}`;
  }
  return formatWindow(window, locale);
}

/**
 * The three stretches on their own, for callers that only need the dates — the
 * deploys page filters by window and has no items to count.
 */
export function buildTemplateWindows(
  now: Date,
  locale: string,
  sprint: SprintConfig = DEFAULT_SPRINT_CONFIG,
): ChangelogTemplateWindow[] {
  const windows: Record<ChangelogTemplateId, DateWindow> = {
    week: trailingWindow(now, 7),
    fortnight: trailingWindow(now, 14),
    sprint: getSprintAt(now, sprint).window,
  };

  return CHANGELOG_TEMPLATE_ORDER.map((id) => ({
    id,
    window: windows[id],
    title: templateTitle(id, windows[id], locale, sprint),
  }));
}

export function buildTemplates(
  entries: ChangelogCandidateEntry[],
  now: Date,
  locale: string,
  sprint: SprintConfig = DEFAULT_SPRINT_CONFIG,
): ChangelogTemplate[] {
  return buildTemplateWindows(now, locale, sprint).map((template) => {
    const covered = entries.filter((entry) =>
      isWithinWindow(entry.deployedAt, template.window),
    );

    return {
      ...template,
      entryCount: covered.length,
      itemCount: covered.reduce((sum, entry) => sum + entry.items.length, 0),
    };
  });
}

export interface TimelineDay<T> {
  /** Local calendar date, used as the React key and the toggle target. */
  key: string;
  date: Date;
  entries: T[];
}

export interface DeployTimeline<T> {
  days: TimelineDay<T>[];
  axis: DateWindow;
}

/** Shortest axis worth drawing — a single deploy should not fill the whole bar. */
const MIN_AXIS_DAYS = 7;

/**
 * Local calendar date as `YYYY-MM-DD`. Built by hand rather than through
 * `toISOString`, which would shift a local midnight into the previous day for
 * anyone east of UTC and bucket their evening deploys under yesterday.
 */
export function toDayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Candidate deploys bucketed by the day they landed, with an axis wide enough
 * to hold both them and the window a template picked — otherwise choosing
 * "Sprint 2" would highlight a band running off the end of the chart.
 */
export function buildTimeline<T extends { deployedAt: string }>(
  entries: T[],
  window: DateWindow | null,
): DeployTimeline<T> | null {
  if (entries.length === 0) return null;

  const byDay = new Map<string, TimelineDay<T>>();
  for (const entry of entries) {
    const date = startOfDay(new Date(entry.deployedAt));
    const key = toDayKey(date);
    const day = byDay.get(key) ?? { key, date, entries: [] };
    day.entries.push(entry);
    byDay.set(key, day);
  }

  const days = [...byDay.values()].sort((a, b) => a.date.getTime() - b.date.getTime());
  const first = days[0]!.date;
  const last = days[days.length - 1]!.date;

  let start = window ? new Date(Math.min(first.getTime(), window.start.getTime())) : first;
  let end = window
    ? new Date(Math.max(addDays(last, 1).getTime(), window.end.getTime()))
    : addDays(last, 1);

  const short = MIN_AXIS_DAYS - daysBetween(start, end);
  if (short > 0) {
    start = addDays(start, -Math.ceil(short / 2));
    end = addDays(end, Math.floor(short / 2));
  }

  return { days, axis: { start, end } };
}

/**
 * Every calendar day on the axis, quiet ones included. A fortnight of nothing
 * followed by six deploys in an afternoon is the shape worth seeing, and it
 * only exists if the empty days take up room.
 */
export function expandTimelineDays<T>(
  timeline: DeployTimeline<T>,
): TimelineDay<T>[] {
  const byDay = new Map(timeline.days.map((day) => [day.key, day]));
  const total = daysBetween(timeline.axis.start, timeline.axis.end);

  return Array.from({ length: total }, (_, index) => {
    const date = addDays(timeline.axis.start, index);
    const key = toDayKey(date);
    return byDay.get(key) ?? { key, date, entries: [] };
  });
}

/**
 * A run of consecutive days on the chart's axis that one roundup covers, or —
 * when `title` is null — the deploys since the last roundup that nothing has
 * rolled up yet. Indices address the array `expandTimelineDays` returned, so
 * the rail can be laid out on the same grid as the columns.
 */
export interface RoundupCoverageSegment {
  title: string | null;
  /** Inclusive. */
  startIndex: number;
  /** Inclusive. */
  endIndex: number;
  /** Deploys in the run. Only worth showing for the un-announced tail. */
  count: number;
}

/**
 * Which stretches of the timeline have been announced, and what has piled up
 * since. A roundup spans from its earliest covered deploy to its latest,
 * quiet days in the middle included — it covered that whole window, and a
 * rail broken by every Sunday would read as three roundups rather than one.
 *
 * Callers pass `null` when roundups are unreadable — an absent map is not the
 * same as an empty one, and claiming "9 not in a roundup" to somebody who
 * cannot see roundups would be a lie.
 */
export function buildRoundupCoverage<T extends { versionTag: string }>(
  days: TimelineDay<T>[],
  titleByVersion: ReadonlyMap<string, string> | null,
): RoundupCoverageSegment[] {
  if (titleByVersion === null) return [];

  const spans = new Map<string, RoundupCoverageSegment>();
  days.forEach((day, index) => {
    for (const entry of day.entries) {
      const title = titleByVersion.get(entry.versionTag);
      if (title === undefined) continue;
      const span = spans.get(title);
      if (span === undefined) {
        spans.set(title, { title, startIndex: index, endIndex: index, count: 1 });
      } else {
        span.endIndex = index;
        span.count += 1;
      }
    }
  });

  // Overlapping roundups would collide in a single rail row. They should not
  // occur — a deploy belongs to one roundup — so the later one is dropped
  // rather than given a row of its own nobody would know how to read.
  const announced: RoundupCoverageSegment[] = [];
  for (const span of [...spans.values()].sort((a, b) => a.startIndex - b.startIndex)) {
    const previous = announced[announced.length - 1];
    if (previous && span.startIndex <= previous.endIndex) continue;
    announced.push(span);
  }

  const lastAnnounced = announced[announced.length - 1]?.endIndex ?? -1;
  const pending = days.reduce<{ start: number; end: number; count: number } | null>(
    (run, day, index) => {
      if (index <= lastAnnounced) return run;
      const uncovered = day.entries.filter(
        (entry) => !titleByVersion.has(entry.versionTag),
      ).length;
      if (uncovered === 0) return run;
      if (run === null) return { start: index, end: index, count: uncovered };
      return { start: run.start, end: index, count: run.count + uncovered };
    },
    null,
  );

  if (pending === null) return announced;
  return [
    ...announced,
    {
      title: null,
      startIndex: pending.start,
      endIndex: pending.end,
      count: pending.count,
    },
  ];
}

/** Where a moment sits on the axis, 0–100, for absolute positioning. */
export function axisPercent(at: Date, axis: DateWindow): number {
  const span = axis.end.getTime() - axis.start.getTime();
  if (span <= 0) return 0;
  const ratio = (at.getTime() - axis.start.getTime()) / span;
  return Math.min(100, Math.max(0, ratio * 100));
}
